/**
 * CampaignManager — Google Ads Kampagnen-Verwaltung pro Firma.
 * Liste mit Metriken (30 Tage), Pause/Aktivieren, Budget aendern,
 * Keyword-Verwaltung, Kampagnen-Erstellung mit KI-Anzeigentexten.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Loader2, Play, Pause, Pencil, RefreshCw, Plus, Trash2, KeyRound,
  Sparkles, ChevronDown, ChevronRight, AlertTriangle, Megaphone, ExternalLink, Lightbulb,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SEVERITY_STYLE = {
  high: { cls: "border-red-200 bg-red-50", icon: "🔴" },
  medium: { cls: "border-amber-200 bg-amber-50", icon: "🟡" },
  info: { cls: "border-blue-200 bg-blue-50", icon: "ℹ️" },
};

const STATUS_BADGE = {
  ENABLED: "bg-emerald-100 text-emerald-700 border-emerald-200",
  PAUSED: "bg-amber-100 text-amber-700 border-amber-200",
};
const STATUS_LABEL = { ENABLED: "Aktiv", PAUSED: "Pausiert" };
const MATCH_LABEL = { EXACT: "Exakt", PHRASE: "Passende Wortgruppe", BROAD: "Weitgehend" };

export default function CampaignManager({ companyId }) {
  const [status, setStatus] = useState(null);
  const [campaigns, setCampaigns] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [keywords, setKeywords] = useState({});
  const [kwOpen, setKwOpen] = useState({}); // `${campaignId}-${groupId}` -> bool
  const [renameEdit, setRenameEdit] = useState(null); // {campaign, value}
  const [ads, setAds] = useState({}); // campaignId -> [ads]
  const [adEdit, setAdEdit] = useState(null); // {campaignId, ad, headlines, descriptions, final_url}
  const [budgetEdit, setBudgetEdit] = useState(null); // {campaign, value}
  const [newKw, setNewKw] = useState({}); // adGroupId -> {text, match_type}
  const [kwIdeas, setKwIdeas] = useState({}); // adGroupId -> [ideas]
  const [createOpen, setCreateOpen] = useState(false);
  const [autopilot, setAutopilot] = useState("off");
  const [recos, setRecos] = useState(null);
  const [recosLoading, setRecosLoading] = useState(false);
  const [executingReco, setExecutingReco] = useState(null);

  useEffect(() => {
    if (!companyId) return;
    setRecos(null);
    axios.get(`${API}/google-ads/${companyId}/autopilot`, { withCredentials: true })
      .then((r) => setAutopilot(r.data.mode || "off")).catch(() => setAutopilot("off"));
  }, [companyId]);

  const changeAutopilot = async (mode) => {
    try {
      const r = await axios.post(`${API}/google-ads/${companyId}/autopilot`, { mode }, { withCredentials: true });
      setAutopilot(mode);
      toast.success(r.data.message);
    } catch (e) { toast.error(e.response?.data?.detail || "Autopilot-Einstellung fehlgeschlagen"); }
  };

  const fetchRecos = async () => {
    setRecosLoading(true);
    try {
      const r = await axios.post(`${API}/google-ads/${companyId}/ai/recommendations`, {}, { withCredentials: true });
      setRecos(r.data.recommendations || []);
    } catch (e) { toast.error(e.response?.data?.detail || "KI-Empfehlungen fehlgeschlagen"); }
    finally { setRecosLoading(false); }
  };

  const executeReco = async (r, i) => {
    setExecutingReco(i);
    try {
      const res = await axios.post(`${API}/google-ads/${companyId}/ai/recommendations/execute`, {
        campaign_id: r.campaign_id, action: r.suggested_action, reason: r.title,
      }, { withCredentials: true });
      toast.success(res.data.message);
      setRecos((list) => list.map((x, j) => (j === i ? { ...x, executed: true } : x)));
      loadCampaigns();
    } catch (e) { toast.error(e.response?.data?.detail || "Ausfuehren fehlgeschlagen"); }
    finally { setExecutingReco(null); }
  };

  const loadStatus = useCallback(async () => {
    if (!companyId) return;
    try {
      const r = await axios.get(`${API}/google-ads/${companyId}/manager-status`, { withCredentials: true });
      setStatus(r.data);
    } catch { setStatus(null); }
  }, [companyId]);

  const loadCampaigns = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/google-ads/${companyId}/campaigns`, { withCredentials: true });
      setCampaigns(r.data.campaigns || []);
    } catch (e) {
      setCampaigns(null);
      toast.error(e.response?.data?.detail || "Kampagnen laden fehlgeschlagen");
    } finally { setLoading(false); }
  }, [companyId]);

  useEffect(() => {
    setStatus(null); setCampaigns(null); setExpanded(null); setKeywords({}); setAds({});
    loadStatus();
  }, [companyId, loadStatus]);
  useEffect(() => { if (status?.ready) loadCampaigns(); }, [status, loadCampaigns]);

  const toggleCampaign = async (c) => {
    const next = c.status === "ENABLED" ? "PAUSED" : "ENABLED";
    setBusy(`status-${c.id}`);
    try {
      const r = await axios.post(`${API}/google-ads/${companyId}/campaigns/${c.id}/status`,
        { status: next }, { withCredentials: true });
      toast.success(r.data.message);
      setCampaigns((list) => list.map((x) => (x.id === c.id ? { ...x, status: next } : x)));
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
    finally { setBusy(""); }
  };

  const saveBudget = async () => {
    const { campaign, value } = budgetEdit;
    setBusy("budget");
    try {
      const r = await axios.put(`${API}/google-ads/${companyId}/budgets/${campaign.budget_id}`,
        { amount: parseFloat(value) }, { withCredentials: true });
      toast.success(r.data.message);
      setBudgetEdit(null);
      setCampaigns((list) => list.map((x) => (x.id === campaign.id ? { ...x, budget: parseFloat(value) } : x)));
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
    finally { setBusy(""); }
  };

  const loadKeywords = async (campaignId) => {
    try {
      const r = await axios.get(`${API}/google-ads/${companyId}/campaigns/${campaignId}/keywords`, { withCredentials: true });
      setKeywords((k) => ({ ...k, [campaignId]: r.data.ad_groups || [] }));
    } catch (e) { toast.error(e.response?.data?.detail || "Keywords laden fehlgeschlagen"); }
  };

  const loadAds = async (campaignId) => {
    try {
      const r = await axios.get(`${API}/google-ads/${companyId}/campaigns/${campaignId}/ads`, { withCredentials: true });
      setAds((a) => ({ ...a, [campaignId]: r.data.ads || [] }));
    } catch (e) { toast.error(e.response?.data?.detail || "Anzeigen laden fehlgeschlagen"); }
  };

  const toggleExpand = (c) => {
    const next = expanded === c.id ? null : c.id;
    setExpanded(next);
    if (next && !keywords[c.id]) loadKeywords(c.id);
    if (next && !ads[c.id]) loadAds(c.id);
  };

  const saveRename = async () => {
    const { campaign, value } = renameEdit;
    const name = (value || "").trim();
    if (!name) return;
    setBusy("rename");
    try {
      const r = await axios.post(`${API}/google-ads/${companyId}/campaigns/${campaign.id}/rename`,
        { name }, { withCredentials: true });
      toast.success(r.data.message);
      setRenameEdit(null);
      loadCampaigns();
    } catch (e) { toast.error(e.response?.data?.detail || "Umbenennen fehlgeschlagen"); }
    finally { setBusy(""); }
  };

  const adStatus = async (campaignId, ad) => {
    const next = ad.status === "ENABLED" ? "PAUSED" : "ENABLED";
    setBusy(ad.resource_name);
    try {
      const r = await axios.post(`${API}/google-ads/${companyId}/ads/status`,
        { resource_name: ad.resource_name, status: next }, { withCredentials: true });
      toast.success(r.data.message);
      loadAds(campaignId);
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
    finally { setBusy(""); }
  };

  const saveAd = async () => {
    const { campaignId, ad, headlines, descriptions, final_url } = adEdit;
    setBusy("ad-save");
    try {
      const r = await axios.post(`${API}/google-ads/${companyId}/ads/update`, {
        ad_id: ad.ad_id,
        final_url,
        headlines: headlines.filter((h) => h.trim()),
        descriptions: descriptions.filter((d) => d.trim()),
      }, { withCredentials: true });
      toast.success(r.data.message);
      setAdEdit(null);
      loadAds(campaignId);
    } catch (e) { toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen"); }
    finally { setBusy(""); }
  };

  const kwStatus = async (campaignId, kw) => {
    const next = kw.status === "ENABLED" ? "PAUSED" : "ENABLED";
    setBusy(kw.resource_name);
    try {
      await axios.post(`${API}/google-ads/${companyId}/keywords/status`,
        { resource_name: kw.resource_name, status: next }, { withCredentials: true });
      toast.success(next === "ENABLED" ? "Keyword aktiviert" : "Keyword pausiert");
      loadKeywords(campaignId);
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
    finally { setBusy(""); }
  };

  const kwRemove = async (campaignId, kw) => {
    if (!window.confirm(`Keyword "${kw.text}" endgültig entfernen?`)) return;
    setBusy(kw.resource_name);
    try {
      await axios.post(`${API}/google-ads/${companyId}/keywords/remove`,
        { resource_name: kw.resource_name }, { withCredentials: true });
      toast.success("Keyword entfernt");
      loadKeywords(campaignId);
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
    finally { setBusy(""); }
  };

  const kwAdd = async (campaignId, adGroupId) => {
    const v = newKw[adGroupId] || {};
    if (!v.text?.trim()) return;
    setBusy(`add-${adGroupId}`);
    try {
      await axios.post(`${API}/google-ads/${companyId}/adgroups/${adGroupId}/keywords`,
        { text: v.text.trim(), match_type: v.match_type || "PHRASE" }, { withCredentials: true });
      toast.success(`Keyword "${v.text.trim()}" hinzugefügt`);
      setNewKw((s) => ({ ...s, [adGroupId]: { text: "", match_type: v.match_type || "PHRASE" } }));
      loadKeywords(campaignId);
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
    finally { setBusy(""); }
  };

  const genIdeas = async (campaignId, g) => {
    setBusy(`ideas-${g.id}`);
    try {
      const existing = (keywords[campaignId] || []).flatMap((ag) => ag.keywords.map((k) => k.text));
      const r = await axios.post(`${API}/seo/${companyId}/keyword-ideas`,
        { existing_keywords: existing }, { withCredentials: true });
      setKwIdeas((s) => ({ ...s, [g.id]: r.data.ideas || [] }));
    } catch (e) { toast.error(e.response?.data?.detail || "KI-Ideen fehlgeschlagen"); }
    finally { setBusy(""); }
  };

  const addIdea = async (campaignId, adGroupId, idea) => {
    setBusy(`idea-${idea.text}`);
    try {
      await axios.post(`${API}/google-ads/${companyId}/adgroups/${adGroupId}/keywords`,
        { text: idea.text, match_type: idea.match_type }, { withCredentials: true });
      toast.success(`Keyword "${idea.text}" übernommen`);
      setKwIdeas((s) => ({ ...s, [adGroupId]: (s[adGroupId] || []).filter((x) => x.text !== idea.text) }));
      loadKeywords(campaignId);
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
    finally { setBusy(""); }
  };

  if (status && !status.ready) {
    return (
      <Card data-testid="gads-mgr-not-ready">
        <CardContent className="py-6 flex items-start gap-3 text-sm">
          <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
          <div>
            <p className="font-medium">Kampagnen-Verwaltung noch nicht bereit</p>
            <p className="text-[var(--studio-text-muted)] mt-1">
              Es fehlt: {status.missing.join(", ")}. Bitte im Tab «Verbindung &amp; Conversions» ergänzen.
              Hinweis: Seit dem 9.9.2026 braucht es keinen Developer-Token mehr — der API-Zugriff hängt am
              Google-Cloud-Projekt deiner OAuth-Client-ID (Google Cloud Console → Google Ads API Übersicht).
            </p>
            <p className="text-xs text-[var(--studio-text-muted)] mt-2">{status.scope_hint}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="gads-campaign-manager">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="text-base font-semibold">Kampagnen</h2>
        <span className="text-xs text-[var(--studio-text-muted)]">Metriken: letzte 30 Tage</span>
        <div className="flex-1" />
        <span className="inline-flex items-center gap-1.5 text-xs" title="Jeden Montag analysiert die KI automatisch alle Google-Kampagnen. Vorschlagen = E-Mail mit Empfehlungen. Vollautomatik = schwache Kampagnen werden zusätzlich automatisch pausiert (Undo in KI-Historie).">
          🤖 Autopilot
          <Select value={autopilot} onValueChange={changeAutopilot}>
            <SelectTrigger className="h-8 w-44 text-xs" data-testid="gads-autopilot-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="off">Aus</SelectItem>
              <SelectItem value="suggest">Wöchentlich vorschlagen</SelectItem>
              <SelectItem value="auto">Vollautomatik (pausieren)</SelectItem>
              <SelectItem value="experiment">🧪 Test-Modus (A/B-Experimente)</SelectItem>
            </SelectContent>
          </Select>
        </span>
        <Button variant="outline" size="sm" className="gap-1" onClick={fetchRecos} disabled={recosLoading} data-testid="gads-ai-recommendations-btn">
          {recosLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Lightbulb className="w-3.5 h-3.5" />}
          KI-Empfehlungen
        </Button>
        <Button variant="outline" size="sm" onClick={loadCampaigns} disabled={loading} data-testid="gads-mgr-refresh">
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
        </Button>
        <Button size="sm" onClick={() => setCreateOpen(true)} className="gap-1" data-testid="gads-mgr-create-btn">
          <Plus className="w-4 h-4" /> Neue Kampagne
        </Button>
      </div>

      {recos && (
        <Card data-testid="gads-ai-recommendations-panel">
          <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Lightbulb className="w-4 h-4 text-amber-500" /> KI-Empfehlungen (Google Ads)</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {recos.map((r, i) => {
              const style = SEVERITY_STYLE[r.severity] || SEVERITY_STYLE.info;
              const canExecute = (r.suggested_action === "pause" || r.suggested_action === "activate") && r.campaign_id && !r.executed;
              const isBudget = ["increase_budget", "decrease_budget"].includes(r.suggested_action);
              return (
                <div key={i} className={`border rounded-lg p-3 text-sm ${style.cls}`} data-testid={`gads-reco-item-${i}`}>
                  <div className="font-medium">{style.icon} {r.title}</div>
                  <div className="text-xs mt-1 text-gray-700">{r.recommendation}</div>
                  <div className="flex flex-wrap items-center gap-2 mt-2">
                    {canExecute && (
                      <Button size="sm" variant="outline" className="h-7 text-xs gap-1"
                        disabled={executingReco === i}
                        onClick={() => executeReco(r, i)}
                        data-testid={`gads-reco-execute-${i}`}>
                        {executingReco === i ? <Loader2 className="w-3 h-3 animate-spin" />
                          : r.suggested_action === "pause" ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />}
                        {r.suggested_action === "pause" ? "Kampagne pausieren" : "Kampagne aktivieren"}
                      </Button>
                    )}
                    {r.executed && <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200">Ausgeführt</Badge>}
                    {isBudget && <span className="text-[10px] text-gray-500">🔒 Budgetänderungen sind für die KI gesperrt — bitte manuell im Budget-Editor</span>}
                  </div>
                </div>
              );
            })}
            {recos.length === 0 && <p className="text-sm text-[var(--studio-text-muted)]">Keine Empfehlungen.</p>}
          </CardContent>
        </Card>
      )}

      {loading && !campaigns && (
        <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[var(--studio-text-muted)]" /></div>
      )}
      {campaigns?.length === 0 && (
        <Card><CardContent className="py-8 text-center text-sm text-[var(--studio-text-muted)]" data-testid="gads-mgr-empty">
          Keine Kampagnen im Konto. Erstelle die erste über «Neue Kampagne».
        </CardContent></Card>
      )}

      {campaigns?.map((c) => (
        <Card key={c.id} data-testid={`gads-campaign-${c.id}`}>
          <CardContent className="py-3 space-y-3">
            <div className="flex flex-wrap items-center gap-3">
              <button onClick={() => toggleExpand(c)} className="flex items-center gap-1 font-medium text-sm" data-testid={`gads-campaign-expand-${c.id}`}>
                {expanded === c.id ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                {c.name}
              </button>
              <Button variant="ghost" size="sm" className="h-6 px-1.5 text-[var(--studio-text-muted)]"
                title="Kampagne umbenennen"
                onClick={() => setRenameEdit({ campaign: c, value: c.name })}
                data-testid={`gads-rename-${c.id}`}>
                <Pencil className="w-3 h-3" />
              </Button>
              <Badge variant="outline" className={`text-[10px] ${STATUS_BADGE[c.status] || ""}`}>{STATUS_LABEL[c.status] || c.status}</Badge>
              <Badge variant="outline" className="text-[10px]">{c.channel}</Badge>
              <div className="flex-1" />
              <div className="flex items-center gap-4 text-xs text-[var(--studio-text-muted)]">
                <span><b className="text-[var(--studio-text)]">{c.budget.toFixed(2)}</b>/Tag</span>
                <span><b className="text-[var(--studio-text)]">{c.clicks}</b> Klicks</span>
                <span><b className="text-[var(--studio-text)]">{c.impressions}</b> Impr.</span>
                <span><b className="text-[var(--studio-text)]">{c.cost.toFixed(2)}</b> Kosten</span>
                <span><b className="text-[var(--studio-text)]">{c.conversions}</b> Conv.</span>
              </div>
              <Button variant="outline" size="sm" className="h-7 gap-1"
                onClick={() => setBudgetEdit({ campaign: c, value: String(c.budget) })}
                data-testid={`gads-budget-edit-${c.id}`}>
                <Pencil className="w-3 h-3" /> Budget
              </Button>
              <Button size="sm" className="h-7 gap-1" variant={c.status === "ENABLED" ? "outline" : "default"}
                onClick={() => toggleCampaign(c)} disabled={busy === `status-${c.id}`}
                data-testid={`gads-toggle-${c.id}`}>
                {busy === `status-${c.id}` ? <Loader2 className="w-3 h-3 animate-spin" />
                  : c.status === "ENABLED" ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />}
                {c.status === "ENABLED" ? "Pausieren" : "Aktivieren"}
              </Button>
            </div>

            {expanded === c.id && (
              <div className="border-t border-[var(--studio-border)] pt-3 space-y-3" data-testid={`gads-keywords-${c.id}`}>
                {!keywords[c.id] && <Loader2 className="w-4 h-4 animate-spin" />}
                {keywords[c.id]?.length === 0 && (
                  <p className="text-xs text-[var(--studio-text-muted)]">Keine Anzeigengruppen/Keywords.</p>
                )}
                {keywords[c.id]?.map((g) => (
                  <div key={g.id} className="space-y-1.5">
                    <div className="flex items-center gap-2 text-xs font-medium">
                      <KeyRound className="w-3.5 h-3.5" /> {g.name}
                      <Badge variant="outline" className={`text-[9px] ${STATUS_BADGE[g.status] || ""}`}>{STATUS_LABEL[g.status] || g.status}</Badge>
                    </div>
                    {(ads[c.id] || []).filter((a) => a.ad_group_id === g.id).map((a) => (
                      <div key={a.resource_name} className="border border-[var(--studio-border)] rounded-lg px-3 py-2 ml-5 space-y-1 bg-[var(--studio-bg)]" data-testid={`gads-ad-${a.ad_id}`}>
                        <div className="flex flex-wrap items-center gap-2 text-xs">
                          <Megaphone className="w-3.5 h-3.5 text-[#2563EB]" />
                          <span className="font-medium">Anzeige (RSA)</span>
                          <Badge variant="outline" className={`text-[9px] ${STATUS_BADGE[a.status] || ""}`}>{STATUS_LABEL[a.status] || a.status}</Badge>
                          <div className="flex-1" />
                          <Button variant="ghost" size="sm" className="h-6 px-1.5" disabled={busy === a.resource_name}
                            onClick={() => adStatus(c.id, a)} data-testid={`gads-ad-toggle-${a.ad_id}`}>
                            {busy === a.resource_name ? <Loader2 className="w-3 h-3 animate-spin" />
                              : a.status === "ENABLED" ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />}
                          </Button>
                          <Button variant="ghost" size="sm" className="h-6 px-1.5"
                            onClick={() => setAdEdit({
                              campaignId: c.id, ad: a,
                              headlines: [...a.headlines, ...Array(Math.max(0, 3 - a.headlines.length)).fill("")],
                              descriptions: [...a.descriptions, ...Array(Math.max(0, 2 - a.descriptions.length)).fill("")],
                              final_url: a.final_urls[0] || "",
                            })}
                            data-testid={`gads-ad-edit-${a.ad_id}`}>
                            <Pencil className="w-3 h-3" />
                          </Button>
                        </div>
                        <p className="text-xs text-[var(--studio-text)]">{a.headlines.join(" | ")}</p>
                        <p className="text-[11px] text-[var(--studio-text-muted)]">{a.descriptions.join(" — ")}</p>
                        {a.final_urls[0] && (
                          <a href={a.final_urls[0]} target="_blank" rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-[11px] text-[#2563EB] hover:underline break-all"
                            data-testid={`gads-ad-url-${a.ad_id}`}>
                            <ExternalLink className="w-3 h-3 shrink-0" /> {a.final_urls[0]}
                          </a>
                        )}
                      </div>
                    ))}
                    <button
                      className="ml-5 flex items-center gap-1.5 text-[11px] text-[var(--studio-text-muted)] hover:text-[var(--studio-text)]"
                      onClick={() => setKwOpen((s) => ({ ...s, [`${c.id}-${g.id}`]: !s[`${c.id}-${g.id}`] }))}
                      data-testid={`gads-kw-toggle-list-${g.id}`}>
                      {kwOpen[`${c.id}-${g.id}`] ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                      <KeyRound className="w-3 h-3" />
                      {g.keywords.length} Keywords {kwOpen[`${c.id}-${g.id}`] ? "zuklappen" : "anzeigen"}
                    </button>
                    {kwOpen[`${c.id}-${g.id}`] && (<>
                    {g.keywords.map((kw) => (
                      <div key={kw.resource_name} className="flex items-center gap-2 border border-[var(--studio-border)] rounded px-2 py-1 text-xs ml-5" data-testid={`gads-kw-${kw.resource_name}`}>
                        <span className="font-medium">{kw.text}</span>
                        <Badge variant="outline" className="text-[9px]">{MATCH_LABEL[kw.match_type] || kw.match_type}</Badge>
                        <span className="text-[10px] text-slate-500 ml-1" data-testid={`gads-kw-stats-${kw.resource_name}`}>
                          <b className="text-slate-700">{kw.clicks ?? 0}</b> Klicks · {kw.impressions ?? 0} Impr. · {(kw.cost ?? 0).toFixed(2)} Kosten <span className="text-slate-400">(30T)</span>
                        </span>
                        <Badge variant="outline" className={`text-[9px] ${STATUS_BADGE[kw.status] || ""}`}>{STATUS_LABEL[kw.status] || kw.status}</Badge>
                        <div className="flex-1" />
                        <Button variant="ghost" size="sm" className="h-6 px-1.5" disabled={busy === kw.resource_name}
                          onClick={() => kwStatus(c.id, kw)} data-testid="gads-kw-toggle">
                          {kw.status === "ENABLED" ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />}
                        </Button>
                        <Button variant="ghost" size="sm" className="h-6 px-1.5 text-red-500" disabled={busy === kw.resource_name}
                          onClick={() => kwRemove(c.id, kw)} data-testid="gads-kw-remove">
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    ))}
                    <div className="flex items-center gap-2 ml-5">
                      <Input className="h-7 text-xs w-56" placeholder="Neues Keyword…"
                        value={newKw[g.id]?.text || ""}
                        onChange={(e) => setNewKw((s) => ({ ...s, [g.id]: { ...(s[g.id] || {}), text: e.target.value } }))}
                        data-testid={`gads-kw-new-input-${g.id}`} />
                      <Select value={newKw[g.id]?.match_type || "PHRASE"}
                        onValueChange={(v) => setNewKw((s) => ({ ...s, [g.id]: { ...(s[g.id] || {}), match_type: v } }))}>
                        <SelectTrigger className="h-7 w-44 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {Object.entries(MATCH_LABEL).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <Button size="sm" className="h-7" disabled={busy === `add-${g.id}` || !newKw[g.id]?.text?.trim()}
                        onClick={() => kwAdd(c.id, g.id)} data-testid={`gads-kw-add-${g.id}`}>
                        {busy === `add-${g.id}` ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
                      </Button>
                      <Button size="sm" variant="outline" className="h-7 gap-1 text-xs" disabled={busy === `ideas-${g.id}`}
                        onClick={() => genIdeas(c.id, g)} data-testid={`gads-kw-ideas-${g.id}`}>
                        {busy === `ideas-${g.id}` ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                        KI-Ideen
                      </Button>
                    </div>
                    {kwIdeas[g.id]?.length > 0 && (
                      <div className="ml-5 flex flex-wrap gap-1.5" data-testid={`gads-kw-ideas-list-${g.id}`}>
                        {kwIdeas[g.id].map((idea) => (
                          <button key={idea.text} title={idea.reason}
                            onClick={() => addIdea(c.id, g.id, idea)} disabled={busy === `idea-${idea.text}`}
                            className="flex items-center gap-1 text-[11px] border border-dashed border-[#2563EB] text-[#2563EB] rounded-full px-2 py-0.5 hover:bg-blue-50"
                            data-testid="gads-kw-idea-chip">
                            <Plus className="w-3 h-3" /> {idea.text}
                            <span className="text-slate-400">({idea.match_type})</span>
                          </button>
                        ))}
                      </div>
                    )}
                    </>)}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      ))}

      {/* Umbenennen-Dialog */}
      <Dialog open={!!renameEdit} onOpenChange={(o) => !o && setRenameEdit(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Kampagne umbenennen</DialogTitle></DialogHeader>
          {renameEdit && (
            <div className="space-y-3">
              <Input value={renameEdit.value} maxLength={120} autoFocus
                onChange={(e) => setRenameEdit((s) => ({ ...s, value: e.target.value }))}
                onKeyDown={(e) => e.key === "Enter" && saveRename()}
                data-testid="gads-rename-input" />
              <p className="text-[11px] text-[var(--studio-text-muted)]">
                Tipp: Schema «Firma | Thema | Typ» verwenden, z.B. «StrongFit | EMS Leads | Suche». Der Name wird direkt in Google Ads geändert.
              </p>
              <Button onClick={saveRename} className="w-full" disabled={busy === "rename" || !renameEdit.value.trim()}
                data-testid="gads-rename-save">
                {busy === "rename" ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null} Umbenennen
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Budget-Dialog */}
      <Dialog open={!!budgetEdit} onOpenChange={(o) => !o && setBudgetEdit(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Tagesbudget ändern</DialogTitle></DialogHeader>
          {budgetEdit && (
            <div className="space-y-3">
              <p className="text-sm text-[var(--studio-text-muted)]">{budgetEdit.campaign.name}</p>
              <Input type="number" step="0.5" min="0.5" value={budgetEdit.value}
                onChange={(e) => setBudgetEdit((b) => ({ ...b, value: e.target.value }))}
                data-testid="gads-budget-input" />
              <Button onClick={saveBudget} disabled={busy === "budget" || !parseFloat(budgetEdit.value)} className="w-full" data-testid="gads-budget-save">
                {busy === "budget" ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null} Speichern
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Anzeige-bearbeiten-Dialog */}
      <Dialog open={!!adEdit} onOpenChange={(o) => !o && setAdEdit(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Anzeige bearbeiten (RSA)</DialogTitle></DialogHeader>
          {adEdit && (
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium">Finale URL *</label>
                <Input value={adEdit.final_url} className="mt-1"
                  onChange={(e) => setAdEdit((s) => ({ ...s, final_url: e.target.value }))}
                  placeholder="https://www.deine-website.ch/" data-testid="gads-ad-edit-url" />
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <p className="text-xs font-medium">Headlines (max 30 Zeichen) — min. 3</p>
                  {adEdit.headlines.map((h, i) => (
                    <Input key={i} value={h} maxLength={30} className="h-8 text-xs"
                      onChange={(e) => setAdEdit((s) => ({ ...s, headlines: s.headlines.map((x, j) => (j === i ? e.target.value : x)) }))}
                      data-testid={`gads-ad-edit-headline-${i}`} />
                  ))}
                  {adEdit.headlines.length < 15 && (
                    <Button size="sm" variant="outline" className="h-7 text-xs gap-1"
                      onClick={() => setAdEdit((s) => ({ ...s, headlines: [...s.headlines, ""] }))}
                      data-testid="gads-ad-edit-add-headline">
                      <Plus className="w-3 h-3" /> Headline
                    </Button>
                  )}
                </div>
                <div className="space-y-1">
                  <p className="text-xs font-medium">Descriptions (max 90 Zeichen) — min. 2</p>
                  {adEdit.descriptions.map((d, i) => (
                    <Textarea key={i} value={d} maxLength={90} rows={2} className="text-xs"
                      onChange={(e) => setAdEdit((s) => ({ ...s, descriptions: s.descriptions.map((x, j) => (j === i ? e.target.value : x)) }))}
                      data-testid={`gads-ad-edit-description-${i}`} />
                  ))}
                  {adEdit.descriptions.length < 4 && (
                    <Button size="sm" variant="outline" className="h-7 text-xs gap-1"
                      onClick={() => setAdEdit((s) => ({ ...s, descriptions: [...s.descriptions, ""] }))}
                      data-testid="gads-ad-edit-add-description">
                      <Plus className="w-3 h-3" /> Description
                    </Button>
                  )}
                </div>
              </div>
              <Button onClick={saveAd} className="w-full" data-testid="gads-ad-edit-save"
                disabled={busy === "ad-save" || !adEdit.final_url.startsWith("http")
                  || adEdit.headlines.filter((h) => h.trim()).length < 3
                  || adEdit.descriptions.filter((d) => d.trim()).length < 2}>
                {busy === "ad-save" ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null} Anzeige speichern
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <CreateCampaignDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        companyId={companyId}
        onCreated={() => { setCreateOpen(false); loadCampaigns(); }}
      />
    </div>
  );
}

export function CreateCampaignDialog({ open, onClose, companyId, onCreated, initial }) {
  const [f, setF] = useState({ name: "", daily_budget: "20", final_url: "", keywords: "", match_type: "PHRASE", brief: "" });
  const [headlines, setHeadlines] = useState([]);
  const [descriptions, setDescriptions] = useState([]);
  const [busy, setBusy] = useState("");
  const [ctaLinks, setCtaLinks] = useState([]);

  useEffect(() => {
    if (!open || !companyId) return;
    axios.get(`${API}/tracking-links`, { params: { company_id: companyId }, withCredentials: true })
      .then((r) => setCtaLinks(r.data?.items || []))
      .catch(() => setCtaLinks([]));
  }, [open, companyId]);

  useEffect(() => {
    if (open && initial) {
      setF({
        name: initial.name || "",
        daily_budget: String(initial.daily_budget ?? "20"),
        final_url: initial.final_url || "",
        keywords: (initial.keywords || []).join("\n"),
        match_type: initial.match_type || "PHRASE",
        brief: initial.brief || "",
      });
      setHeadlines(initial.headlines || []);
      setDescriptions(initial.descriptions || []);
    }
  }, [open, initial]);

  const kwList = f.keywords.split("\n").map((k) => k.trim()).filter(Boolean);

  const genCopy = async () => {
    setBusy("ai");
    try {
      const r = await axios.post(`${API}/google-ads/${companyId}/ai-ad-copy`,
        { brief: f.brief, final_url: f.final_url, keywords: kwList }, { withCredentials: true });
      setHeadlines(r.data.headlines || []);
      setDescriptions(r.data.descriptions || []);
      toast.success("KI-Anzeigentexte erstellt — bitte prüfen/anpassen");
    } catch (e) { toast.error(e.response?.data?.detail || "KI-Generierung fehlgeschlagen"); }
    finally { setBusy(""); }
  };

  const create = async () => {
    setBusy("create");
    try {
      const r = await axios.post(`${API}/google-ads/${companyId}/campaigns`, {
        name: f.name,
        daily_budget: parseFloat(f.daily_budget),
        final_url: f.final_url,
        keywords: kwList.map((k) => ({ text: k, match_type: f.match_type })),
        headlines: headlines.filter((h) => h.trim()),
        descriptions: descriptions.filter((d) => d.trim()),
      }, { withCredentials: true });
      toast.success(r.data.message);
      onCreated();
    } catch (e) { toast.error(e.response?.data?.detail || "Erstellung fehlgeschlagen"); }
    finally { setBusy(""); }
  };

  const editList = (list, setList, i, v) => setList(list.map((x, j) => (j === i ? v : x)));
  const canCreate = f.name.trim() && parseFloat(f.daily_budget) > 0 && f.final_url.startsWith("http")
    && kwList.length > 0 && headlines.filter((h) => h.trim()).length >= 3
    && descriptions.filter((d) => d.trim()).length >= 2;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Neue Search-Kampagne (wird pausiert erstellt)</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium">Kampagnen-Name *</label>
              <Input value={f.name} onChange={(e) => setF((s) => ({ ...s, name: e.target.value }))}
                placeholder="z.B. Suche — Hauptleistung" className="mt-1" data-testid="gads-create-name" />
            </div>
            <div>
              <label className="text-xs font-medium">Tagesbudget (Kontowährung) *</label>
              <Input type="number" step="0.5" min="0.5" value={f.daily_budget}
                onChange={(e) => setF((s) => ({ ...s, daily_budget: e.target.value }))}
                className="mt-1" data-testid="gads-create-budget" />
            </div>
          </div>
          <div>
            <label className="text-xs font-medium">Landing-URL *</label>
            <Input value={f.final_url} onChange={(e) => setF((s) => ({ ...s, final_url: e.target.value }))}
              placeholder="https://www.deine-website.ch/" className="mt-1" data-testid="gads-create-url" />
            {ctaLinks.length > 0 && (
              <div className="mt-1.5">
                <Select value="" onValueChange={(id) => {
                  const l = ctaLinks.find((x) => x.id === id);
                  if (!l) return;
                  const slug = (f.name || l.name).toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
                  const sep = l.target_url.includes("?") ? "&" : "?";
                  setF((s) => ({ ...s, final_url: `${l.target_url}${sep}utm_source=google_ads&utm_medium=cpc&utm_campaign=${slug}&utm_content=${l.code}` }));
                }}>
                  <SelectTrigger className="h-8 text-xs text-slate-500" data-testid="gads-tracking-link-select">
                    <SelectValue placeholder="📊 Tracking-Link der Firma übernehmen (Ziel + Mess-Parameter einsetzen)" />
                  </SelectTrigger>
                  <SelectContent>
                    {ctaLinks.map((l) => (
                      <SelectItem key={l.id} value={l.id}>{l.name} ({l.channel}) → {l.target_url.replace(/^https?:\/\//, "")}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[10px] text-slate-400 mt-0.5">
                  Setzt die Ziel-URL mit Mess-Parametern ein (kein Umleitungs-Link — Google-Ads-konform). Besucher &amp; Buchungen werden dem Link und der Quelle „Google Ads" zugeordnet.
                </p>
              </div>
            )}
          </div>
          <div className="grid sm:grid-cols-[1fr_auto] gap-3 items-end">
            <div>
              <label className="text-xs font-medium">Keywords * (eins pro Zeile)</label>
              <Textarea rows={4} value={f.keywords} onChange={(e) => setF((s) => ({ ...s, keywords: e.target.value }))}
                placeholder={"fitnessstudio zuerich\npersonal training"} className="mt-1" data-testid="gads-create-keywords" />
            </div>
            <div>
              <label className="text-xs font-medium">Match-Typ</label>
              <Select value={f.match_type} onValueChange={(v) => setF((s) => ({ ...s, match_type: v }))}>
                <SelectTrigger className="w-48 mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(MATCH_LABEL).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="border border-[var(--studio-border)] rounded-lg p-3 space-y-2">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-[#2563EB]" />
              <span className="text-sm font-medium">Anzeigentexte (RSA)</span>
              <div className="flex-1" />
              <Button size="sm" variant="outline" onClick={genCopy} disabled={busy === "ai"} className="gap-1" data-testid="gads-create-ai-btn">
                {busy === "ai" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                Mit KI generieren
              </Button>
            </div>
            <Input value={f.brief} onChange={(e) => setF((s) => ({ ...s, brief: e.target.value }))}
              placeholder="Kurz-Briefing für die KI (optional), z.B. Fokus auf Probetraining" data-testid="gads-create-brief" />
            {headlines.length > 0 && (
              <div className="grid sm:grid-cols-2 gap-2">
                <div className="space-y-1">
                  <p className="text-xs font-medium">Headlines (max 30 Zeichen) — min. 3</p>
                  {headlines.map((h, i) => (
                    <Input key={i} value={h} maxLength={30} className="h-8 text-xs"
                      onChange={(e) => editList(headlines, setHeadlines, i, e.target.value)}
                      data-testid={`gads-headline-${i}`} />
                  ))}
                </div>
                <div className="space-y-1">
                  <p className="text-xs font-medium">Descriptions (max 90 Zeichen) — min. 2</p>
                  {descriptions.map((d, i) => (
                    <Textarea key={i} value={d} maxLength={90} rows={2} className="text-xs"
                      onChange={(e) => editList(descriptions, setDescriptions, i, e.target.value)}
                      data-testid={`gads-description-${i}`} />
                  ))}
                </div>
              </div>
            )}
            {headlines.length === 0 && (
              <p className="text-xs text-[var(--studio-text-muted)]">
                Noch keine Anzeigentexte — mit KI generieren (nutzt deine hinterlegten API-Keys) und danach frei anpassen.
              </p>
            )}
          </div>
          <Button onClick={create} disabled={!canCreate || busy === "create"} className="w-full" data-testid="gads-create-submit">
            {busy === "create" ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Plus className="w-4 h-4 mr-1" />}
            Kampagne erstellen (pausiert)
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
